import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ActionIcon, TextInput } from '@mantine/core';

import clsx from 'clsx';
import CloseIcon from '@/assets/icons/content/close.svg';
import SearchIcon from '@/assets/icons/content/search.svg';
import { POSTHOG_EVENTS } from '@/lib/posthog/events';
import { trackEvent } from '@/lib/posthog/helpers';

import classes from './NavBarSearchBar.module.css';

interface SearchBarProps {
  onSearch?: VoidFunction;
  /** Omit outer padding (e.g. mobile sheet already pads the container). */
  dense?: boolean;
}

const NavBarSearchBar = ({ onSearch, dense }: SearchBarProps) => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSearch = () => {
    if (!search.length) return;

    trackEvent(POSTHOG_EVENTS.SEARCH_GLOBAL_QUERY, {
      query: search,
      activeTab: searchParams.get('tab') ?? null,
    });

    searchParams.delete('q');
    searchParams.delete('p');

    const rest = searchParams.toString();

    onSearch?.();
    navigate(`/search?q=${encodeURIComponent(search)}${rest ? `&${rest}` : ''}`);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const inputValue = search.trim();
    const inputValueLength = inputValue.length;

    if (event.key === 'Enter' && inputValueLength > 0 && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className={clsx(classes.root, dense && classes.rootDense)}>
      <TextInput
        classNames={{
          root: classes.fieldRoot,
          wrapper: classes.fieldWrapper,
          input: classes.input,
          section: classes.section,
        }}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        role="searchbox"
        id="mercury-search"
        aria-label="Search"
        placeholder="Search"
        leftSection={
          <span className={classes.leadingIcon} aria-hidden>
            <SearchIcon />
          </span>
        }
        leftSectionPointerEvents="none"
        rightSection={
          search ? (
            <ActionIcon
              variant="subtle"
              className={classes.clearButton}
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <CloseIcon />
            </ActionIcon>
          ) : null
        }
      />
    </div>
  );
};

export default NavBarSearchBar;
